"use client";

import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ShoppingCart,
  Plus,
  Trash2,
  MapPin,
  User,
  Package,
  CreditCard,
  Receipt,
  DollarSign,
  Warehouse,
  Info,
  Check,
  AlertCircle,
} from "lucide-react";
import { CustomerSelector } from "@/components/common/CustomerSelector";
import { ProductSelector } from "@/components/common/ProductSelector";
import { StateCitySelector } from "@/components/common/StateCitySelector";
import {
  StandardFormInput,
  StandardFormSelect,
} from "@/components/common/StandardFormInput";
import { getProducts } from "@/store/actions/products.actions";
import { getCustomerById } from "@/services/customerService";
import { getPickupLocations } from "@/services/pickupLocationService";
import { Variant } from "@/types/models/product";
import { CustomerAddress } from "@/types/models/customer";
import { cn } from "@/lib/utils";
import { formatCurrency } from "@/utils/currency";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// Types
interface PickupAddress {
  id: string;
  name: string;
  address: {
    address_line_one: string;
    address_line_two?: string;
    city: string;
    state: string;
    postal_code: string;
  };
  is_active: boolean;
  is_default?: boolean;
}

interface ProductLineItem {
  id: string;
  product_id: string;
  product_title?: string;
  variant_id: string;
  variant_title: string;
  quantity: number;
  unit_price: number;
  discount: number;
  discount_type: "percentage" | "fixed";
  tax_rate: number;
  hsn_code: string;
  is_temporary: boolean;
  // For temporary products
  temp_name?: string;
  temp_sku?: string;
}

interface AdditionalCharges {
  shipping_charge: number;
  delivery_charge: number;
  gift_wrap_charge: number;
  transaction_charge: number;
  total_discount: number;
  discount_type: "percentage" | "fixed";
}

interface PackageDetails {
  weight: number;
  weight_unit: "g" | "kg";
  length: number;
  breadth: number;
  height: number;
}

interface TempPickupAddress {
  name: string;
  contact_name: string;
  phone_number: string;
  address_line_one: string;
  address_line_two: string;
  city: string;
  state: string;
  postal_code: string;
}

interface NewCustomer {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
}

interface CreateOrderData {
  pickup_address_id: string;
  use_temp_pickup: boolean;
  temp_pickup_address?: TempPickupAddress;
  customer_id: string;
  is_new_customer: boolean;
  new_customer?: NewCustomer;
  customer_address: string | Record<string, string>;
  use_existing_address: boolean;
  payment_type: "PREPAID" | "POSTPAID" | "SEMIPREPAID";
  semi_prepaid_amount?: number;
  products: Array<{
    variant_id: string;
    product_id: string;
    quantity: number;
    unit_price?: number;
    discount?: number;
    tax_rate?: number;
    hsn_code?: string;
    is_temporary?: boolean;
    temp_name?: string;
    temp_sku?: string;
  }>;
  additional_charges: AdditionalCharges;
  package_details: PackageDetails;
  shipping_type: "shiphouse" | "manual";
}

interface CreateOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: CreateOrderData) => void;
}

// Helper components
const SectionHeader = ({
  icon: Icon,
  title,
  subtitle,
  color,
  step,
}: {
  icon: React.ElementType;
  title: string;
  subtitle?: string;
  color: string;
  step: number;
}) => (
  <div className="flex items-start gap-3">
    <div
      className={cn(
        "flex items-center justify-center w-8 h-8 rounded-lg text-white text-sm font-medium",
        color
      )}
    >
      {step}
    </div>
    <div className="flex-1">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium text-foreground">{title}</span>
      </div>
      {subtitle && (
        <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
      )}
    </div>
  </div>
);

const InfoTooltip = ({ text }: { text: string }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help ml-1" />
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <p className="text-xs">{text}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

export function CreateOrderModal({
  isOpen,
  onClose,
  onCreate,
}: CreateOrderModalProps) {
  // Pickup Address State
  const [pickupAddresses, setPickupAddresses] = useState<PickupAddress[]>([]);
  const [selectedPickupAddressId, setSelectedPickupAddressId] = useState<string>("");
  const [loadingPickupAddresses, setLoadingPickupAddresses] = useState(false);
  const [useTempPickup, setUseTempPickup] = useState(true);
  const [tempPickupAddress, setTempPickupAddress] = useState<TempPickupAddress>({
    name: "Test Warehouse",
    contact_name: "Test User",
    phone_number: "9999999999",
    address_line_one: "123 Temp Street",
    address_line_two: "",
    city: "Dabwali",
    state: "Haryana",
    postal_code: "123456",
  });

  // Customer State
  const [customerId, setCustomerId] = useState<string>("");
  const [customerDetails, setCustomerDetails] = useState<any>(null);
  const [selectedCustomerAddresses, setSelectedCustomerAddresses] = useState<CustomerAddress[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<string>("");
  const [useExistingAddress, setUseExistingAddress] = useState(false);
  const [isNewCustomer, setIsNewCustomer] = useState(true);
  const [newCustomer, setNewCustomer] = useState<NewCustomer>({
    first_name: "Temp",
    last_name: "Customer",
    email: "temp@example.com",
    phone_number: "9999999999",
  });

  // New address fields
  const [newAddress, setNewAddress] = useState({
    address_line_one: "123 Temp Street",
    address_line_two: "",
    city: "Dabwali",
    state: "Haryana",
    postal_code: "123456",
  });

  // Product State
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [productLineItems, setProductLineItems] = useState<ProductLineItem[]>([
    {
      id: "temp-1",
      product_id: "temp-product",
      variant_id: "temp-variant",
      variant_title: "Temp Variant",
      quantity: 1,
      unit_price: 10000,
      discount: 0,
      discount_type: "fixed",
      tax_rate: 0,
      hsn_code: "",
      is_temporary: true,
      temp_name: "Temporary Product",
      temp_sku: "TEMP-SKU",
    },
  ]);
  const [availableVariants, setAvailableVariants] = useState<Record<string, Variant[]>>({});
  const [productTitles, setProductTitles] = useState<Record<string, string>>({});
  const [showAddTempProduct, setShowAddTempProduct] = useState(true);
  const [tempProduct, setTempProduct] = useState({
    name: "Temporary Product",
    sku: "TEMP-SKU",
    unit_price: 10000,
    quantity: 1,
    tax_rate: 0,
    hsn_code: "",
  });

  // Additional Charges State
  const [additionalCharges, setAdditionalCharges] = useState<AdditionalCharges>({
    shipping_charge: 0,
    delivery_charge: 0,
    gift_wrap_charge: 0,
    transaction_charge: 0,
    total_discount: 0,
    discount_type: "fixed",
  });

  // Payment Method State
  const [paymentType, setPaymentType] = useState<"PREPAID" | "POSTPAID" | "SEMIPREPAID">("PREPAID");
  // Stored in rupees; convert to paise for calculations
  const [semiPrepaidAmount, setSemiPrepaidAmount] = useState<number>(0);

  // Accordion State
  const [openSections, setOpenSections] = useState<string[]>(["pickup", "customer"]);

  // Errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  const semiPrepaidPaise = useMemo(
    () => Math.max(0, Math.round(semiPrepaidAmount * 100)),
    [semiPrepaidAmount]
  );

  const getVariantLabel = useCallback((variant?: Variant | null) => {
    if (!variant) return "Variant";
    const optionLabel = [variant.option1, variant.option2, variant.option3]
      .filter(Boolean)
      .join(" / ");
    return (variant.title && variant.title.trim()) || optionLabel || "Variant";
  }, []);

  // Calculate order totals
  const orderCalculations = useMemo(() => {
    let itemsSubtotal = 0;
    let totalTax = 0;
    let totalItemDiscount = 0;

    productLineItems.forEach((item) => {
      const itemTotal = item.unit_price * item.quantity;
      const itemDiscount =
        item.discount_type === "percentage"
          ? (itemTotal * item.discount) / 100
          : item.discount;
      const taxableAmount = itemTotal - itemDiscount;
      const itemTax = (taxableAmount * item.tax_rate) / 100;

      itemsSubtotal += itemTotal;
      totalItemDiscount += itemDiscount;
      totalTax += itemTax;
    });

    const orderDiscount =
      additionalCharges.discount_type === "percentage"
        ? (itemsSubtotal * additionalCharges.total_discount) / 100
        : additionalCharges.total_discount;

    const totalCharges =
      additionalCharges.shipping_charge +
      additionalCharges.delivery_charge +
      additionalCharges.gift_wrap_charge +
      additionalCharges.transaction_charge;

    const grandTotal =
      itemsSubtotal - totalItemDiscount - orderDiscount + totalTax + totalCharges;

    return {
      itemsSubtotal,
      totalItemDiscount,
      orderDiscount,
      totalTax,
      totalCharges,
      grandTotal: Math.max(0, grandTotal),
    };
  }, [productLineItems, additionalCharges]);

  // Load pickup addresses on mount
  useEffect(() => {
    if (isOpen) {
      loadPickupAddresses();
    }
  }, [isOpen]);

  const loadPickupAddresses = async () => {
    setLoadingPickupAddresses(true);
    try {
      const response = await getPickupLocations({
        page: 1,
        page_size: 100,
        is_active: true,
      });
      if (response?.data) {
        const addresses = response.data as PickupAddress[];
        setPickupAddresses(addresses);
        // Auto-select default address
        const defaultAddress = addresses.find((addr) => addr.is_default);
        if (defaultAddress) {
          setSelectedPickupAddressId(defaultAddress.id);
        } else if (addresses.length > 0) {
          setSelectedPickupAddressId(addresses[0].id);
        }
      }
    } catch (error) {
      console.error("Failed to load pickup addresses:", error);
    } finally {
      setLoadingPickupAddresses(false);
    }
  };

  // When customer is selected, load their details and addresses
  const handleCustomerSelect = async (customer: any) => {
    if (customer && customer.id) {
      try {
        const response = await getCustomerById(customer.id);
        if (response.success && response.data) {
          const fullCustomer = response.data as any;
          setCustomerDetails(fullCustomer);
          const addresses = fullCustomer.customer_addresses || [];
          setSelectedCustomerAddresses(addresses);
          if (addresses.length > 0) {
            setSelectedAddressId(addresses[0].id);
            setUseExistingAddress(true);
          } else {
            setSelectedAddressId("");
            setUseExistingAddress(false);
          }
        }
      } catch (error) {
        console.error("Failed to fetch customer details:", error);
        setSelectedCustomerAddresses([]);
        setSelectedAddressId("");
      }
    } else {
      setCustomerDetails(null);
      setSelectedCustomerAddresses([]);
      setSelectedAddressId("");
    }
  };

  // When products are selected, fetch their variants
  useEffect(() => {
    const fetchVariantsForProducts = async () => {
      const newVariants: Record<string, Variant[]> = { ...availableVariants };
      const updatedTitles: Record<string, string> = { ...productTitles };

      for (const productId of selectedProducts) {
        if (!newVariants[productId]) {
          try {
            const response = await getProducts({ page: 1, pageSize: 100 });
            if (response.success && response.data) {
              const product = response.data.find((p) => p.id === productId);
              if (product && product.variants) {
                newVariants[productId] = product.variants as Variant[];
                updatedTitles[productId] = product.title || "Product";
              }
            }
          } catch (error) {
            console.error("Failed to fetch variants:", error);
          }
        }
      }

      setAvailableVariants(newVariants);
      setProductTitles(updatedTitles);

      // Automatically add newly selected products to line items
      selectedProducts.forEach((productId) => {
        if (
          !productLineItems.find((item) => item.product_id === productId) &&
          newVariants[productId] &&
          newVariants[productId].length > 0
        ) {
          const firstVariant = newVariants[productId][0];
          const productTitle = updatedTitles[productId] || "Product";
          const newItem: ProductLineItem = {
            id: `item-${Date.now()}-${Math.random()}`,
            product_id: productId,
            product_title: productTitle,
            variant_id: firstVariant.id || "",
            variant_title: getVariantLabel(firstVariant),
            quantity: 1,
            unit_price: firstVariant.price || 0,
            discount: 0,
            discount_type: "fixed",
            tax_rate: 0,
            hsn_code: "",
            is_temporary: false,
          };
          setProductLineItems((prev) => [...prev, newItem]);
        }
      });

      // Remove line items for deselected products (except temporary)
      setProductLineItems((prev) =>
        prev.filter(
          (item) => item.is_temporary || selectedProducts.includes(item.product_id)
        )
      );
    };

    if (selectedProducts.length > 0) {
      fetchVariantsForProducts();
    }
  }, [selectedProducts]);

  // Update line item
  const updateLineItem = (
    id: string,
    field: keyof ProductLineItem,
    value: any
  ) => {
    setProductLineItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;

        if (field === "variant_id") {
          const variant = availableVariants[item.product_id]?.find(
            (v) => v.id === value
          );
          if (variant) {
            return {
              ...item,
              variant_id: value,
              variant_title: getVariantLabel(variant),
              unit_price: variant.price || 0,
            };
          }
        }

        return { ...item, [field]: value };
      })
    );
  };

  // Remove line item
  const removeLineItem = (id: string) => {
    const item = productLineItems.find((i) => i.id === id);
    if (item && !item.is_temporary) {
      setSelectedProducts((prev) => prev.filter((p) => p !== item.product_id));
    }
    setProductLineItems((prev) => prev.filter((i) => i.id !== id));
  };

  // Add temporary product
  const addTempProduct = () => {
    if (!tempProduct.name || tempProduct.unit_price <= 0) return;

    const newItem: ProductLineItem = {
      id: `temp-${Date.now()}`,
      product_id: "",
      product_title: tempProduct.name,
      variant_id: "",
      variant_title: tempProduct.name,
      quantity: tempProduct.quantity,
      unit_price: tempProduct.unit_price,
      discount: 0,
      discount_type: "fixed",
      tax_rate: tempProduct.tax_rate,
      hsn_code: tempProduct.hsn_code,
      is_temporary: true,
      temp_name: tempProduct.name,
      temp_sku: tempProduct.sku,
    };

    setProductLineItems((prev) => [...prev, newItem]);
    setTempProduct({
      name: "",
      sku: "",
      unit_price: 0,
      quantity: 1,
      tax_rate: 0,
      hsn_code: "",
    });
    setShowAddTempProduct(false);
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Pickup address validation
    if (useTempPickup) {
      if (!tempPickupAddress.name) {
        newErrors.pickup_name = "Pickup location name is required";
      }
      if (!tempPickupAddress.contact_name) {
        newErrors.pickup_contact = "Contact name is required";
      }
      if (!tempPickupAddress.phone_number) {
        newErrors.pickup_phone = "Phone number is required";
      }
      if (!tempPickupAddress.address_line_one) {
        newErrors.pickup_address = "Address is required";
      }
      if (!tempPickupAddress.city) {
        newErrors.pickup_city = "City is required";
      }
      if (!tempPickupAddress.state) {
        newErrors.pickup_state = "State is required";
      }
      if (!tempPickupAddress.postal_code) {
        newErrors.pickup_postal = "Postal code is required";
      }
    } else if (!selectedPickupAddressId) {
      newErrors.pickup = "Please select a pickup address";
    }

    // Customer validation
    if (isNewCustomer) {
      if (!newCustomer.first_name) {
        newErrors.customer_first_name = "First name is required";
      }
      if (!newCustomer.phone_number) {
        newErrors.customer_phone = "Phone number is required";
      }
    } else if (!customerId) {
      newErrors.customer = "Please select a customer";
    }

    // Delivery address validation
    if (useExistingAddress && !isNewCustomer && !selectedAddressId) {
      newErrors.address = "Please select a delivery address";
    }

    if (!useExistingAddress || isNewCustomer) {
      if (!newAddress.address_line_one) {
        newErrors.address_line_one = "Address line 1 is required";
      }
      if (!newAddress.city) {
        newErrors.city = "City is required";
      }
      if (!newAddress.state) {
        newErrors.state = "State is required";
      }
      if (!newAddress.postal_code) {
        newErrors.postal_code = "Postal code is required";
      }
    }

    if (productLineItems.length === 0) {
      newErrors.products = "Please add at least one product";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle create order
  const handleCreate = () => {
    if (!validateForm()) {
      return;
    }

    submitOrder();
  };

  const submitOrder = async () => {
    const orderData: CreateOrderData = {
      pickup_address_id: useTempPickup ? "" : selectedPickupAddressId,
      use_temp_pickup: useTempPickup,
      temp_pickup_address: useTempPickup ? tempPickupAddress : undefined,
      customer_id: isNewCustomer ? "" : customerId,
      is_new_customer: isNewCustomer,
      new_customer: isNewCustomer ? newCustomer : undefined,
      customer_address: (useExistingAddress && !isNewCustomer) ? selectedAddressId : newAddress,
      use_existing_address: useExistingAddress && !isNewCustomer,
      payment_type: paymentType,
      semi_prepaid_amount: paymentType === "SEMIPREPAID" ? semiPrepaidPaise : undefined,
      products: productLineItems.map((item) => ({
        variant_id: item.variant_id,
        product_id: item.product_id,
        quantity: item.quantity,
        unit_price: item.unit_price,
        discount: item.discount,
        tax_rate: item.tax_rate,
        hsn_code: item.hsn_code,
        is_temporary: item.is_temporary,
        temp_name: item.temp_name,
        temp_sku: item.temp_sku,
      })),
      additional_charges: additionalCharges,
      package_details: {
        weight: 500,
        weight_unit: "g",
        length: 10,
        breadth: 10,
        height: 10,
      },
      shipping_type: "shiphouse",
    };

    onCreate(orderData);
    handleClose();
  };

  // Handle close
  const handleClose = () => {
    // Reset all state
    setSelectedPickupAddressId("");
    setUseTempPickup(false);
    setTempPickupAddress({
      name: "",
      contact_name: "",
      phone_number: "",
      address_line_one: "",
      address_line_two: "",
      city: "",
      state: "",
      postal_code: "",
    });
    setCustomerId("");
    setCustomerDetails(null);
    setIsNewCustomer(false);
    setNewCustomer({
      first_name: "",
      last_name: "",
      email: "",
      phone_number: "",
    });
    setSelectedCustomerAddresses([]);
    setSelectedAddressId("");
    setUseExistingAddress(true);
    setNewAddress({
      address_line_one: "",
      address_line_two: "",
      city: "",
      state: "",
      postal_code: "",
    });
    setSelectedProducts([]);
    setProductLineItems([]);
    setAvailableVariants({});
    setProductTitles({});
    setAdditionalCharges({
      shipping_charge: 0,
      delivery_charge: 0,
      gift_wrap_charge: 0,
      transaction_charge: 0,
      total_discount: 0,
      discount_type: "fixed",
    });
    setPaymentType("PREPAID");
    setSemiPrepaidAmount(0);
    setErrors({});
    setOpenSections(["pickup", "customer"]);

    onClose();
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-7xl w-[1500px] h-[90vh] flex flex-col bg-card border border-border/40 shadow-2xl p-0">
          <DialogHeader className="px-6 py-4 border-b border-border/60 bg-muted/30 flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-primary/10 rounded-md">
                <ShoppingCart className="h-4 w-4 text-primary" />
              </div>
              <DialogTitle className="text-lg font-semibold text-foreground">
                Create Manual Order
              </DialogTitle>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Fill in all the details to create a new order. Fields marked with * are required.
            </p>
          </DialogHeader>

          <div className="flex flex-1 min-h-0">
            {/* Main Form Area */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <Accordion
                type="multiple"
                value={openSections}
                onValueChange={setOpenSections}
                className="space-y-3"
              >
                {/* 1. Pickup Address Section */}
                <AccordionItem
                  value="pickup"
                  className="bg-muted/20 rounded-lg border border-border/40 overflow-hidden"
                >
                  <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-muted/30">
                    <SectionHeader
                      icon={Warehouse}
                      title="Pickup Address"
                      subtitle="Select warehouse for order pickup"
                      color="bg-blue-500"
                      step={1}
                    />
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <div className="space-y-3">
                      <p className="text-xs text-muted-foreground flex items-center">
                        <Info className="h-3 w-3 mr-1" />
                        The pickup address is where the courier will collect the package from.
                      </p>

                      {/* Toggle between existing and temporary pickup */}
                      <div className="flex items-center gap-4 mb-3">
                        <label className="flex items-center gap-2 cursor-pointer text-sm">
                          <input
                            type="radio"
                            checked={!useTempPickup}
                            onChange={() => setUseTempPickup(false)}
                            className="w-4 h-4"
                          />
                          Use Saved Address
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer text-sm">
                          <input
                            type="radio"
                            checked={useTempPickup}
                            onChange={() => setUseTempPickup(true)}
                            className="w-4 h-4"
                          />
                          <span className="flex items-center gap-1">
                            Create Temporary Address
                            <Badge
                              variant="secondary"
                              className="text-[9px] h-4 bg-orange-500/15 text-orange-700 border border-orange-500/40 dark:bg-orange-500/25 dark:text-orange-50"
                            >
                              One-time
                            </Badge>
                          </span>
                        </label>
                      </div>

                      {!useTempPickup ? (
                        <>
                          {loadingPickupAddresses ? (
                            <div className="text-sm text-muted-foreground">Loading pickup addresses...</div>
                          ) : pickupAddresses.length === 0 ? (
                            <div className="text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 p-3 rounded-lg">
                              <AlertCircle className="h-4 w-4 inline mr-2" />
                              No pickup addresses found. Please add a pickup address in Settings or create a temporary one.
                            </div>
                          ) : (
                            <RadioGroup
                              value={selectedPickupAddressId}
                              onValueChange={setSelectedPickupAddressId}
                              className="grid gap-2"
                            >
                              {pickupAddresses.map((addr) => (
                                <label
                                  key={addr.id}
                                  className={cn(
                                    "flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all",
                                    selectedPickupAddressId === addr.id
                                      ? "border-primary bg-primary/5"
                                      : "border-border hover:border-primary/50 hover:bg-muted/30"
                                  )}
                                >
                                  <RadioGroupItem value={addr.id} className="mt-1" />
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium text-sm">{addr.name}</span>
                                      {addr.is_default && (
                                        <Badge
                                          variant="secondary"
                                          className="text-[10px] h-4 bg-orange-500/15 text-orange-700 border border-orange-500/40 dark:bg-orange-500/25 dark:text-orange-50"
                                        >
                                          Default
                                        </Badge>
                                      )}
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                      {addr.address.address_line_one}
                                      {addr.address.address_line_two && `, ${addr.address.address_line_two}`}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      {addr.address.city}, {addr.address.state} - {addr.address.postal_code}
                                    </p>
                                  </div>
                                </label>
                              ))}
                            </RadioGroup>
                          )}
                          {errors.pickup && (
                            <p className="text-red-500 text-xs">{errors.pickup}</p>
                          )}
                        </>
                      ) : (
                        <div className="space-y-3">
                          <p className="text-xs font-medium text-foreground flex items-center gap-2">
                            <Plus className="h-3 w-3" />
                            Temporary Pickup Address
                            <span className="text-muted-foreground font-normal">(for this order only)</span>
                          </p>
                          <div className="grid grid-cols-2 gap-3">
                            <StandardFormInput
                              label="Location Name"
                              required
                              value={tempPickupAddress.name}
                              onChange={(e) =>
                                setTempPickupAddress(prev => ({ ...prev, name: e.target.value }))
                              }
                              placeholder="e.g., Temp Warehouse"
                              error={errors.pickup_name}
                              helperText="Name to identify this pickup location"
                            />
                            <StandardFormInput
                              label="Contact Person"
                              required
                              value={tempPickupAddress.contact_name}
                              onChange={(e) =>
                                setTempPickupAddress(prev => ({ ...prev, contact_name: e.target.value }))
                              }
                              placeholder="Full name"
                              error={errors.pickup_contact}
                            />
                            <StandardFormInput
                              label="Phone Number"
                              required
                              value={tempPickupAddress.phone_number}
                              onChange={(e) =>
                                setTempPickupAddress(prev => ({ ...prev, phone_number: e.target.value }))
                              }
                              placeholder="10 digit number"
                              error={errors.pickup_phone}
                            />
                            <div></div>
                            <div className="col-span-2">
                              <StandardFormInput
                                label="Address Line 1"
                                required
                                value={tempPickupAddress.address_line_one}
                                onChange={(e) =>
                                  setTempPickupAddress(prev => ({ ...prev, address_line_one: e.target.value }))
                                }
                                placeholder="Building, street address"
                                error={errors.pickup_address}
                              />
                            </div>
                            <div className="col-span-2">
                              <StandardFormInput
                                label="Address Line 2"
                                value={tempPickupAddress.address_line_two}
                                onChange={(e) =>
                                  setTempPickupAddress(prev => ({ ...prev, address_line_two: e.target.value }))
                                }
                                placeholder="Landmark, area (optional)"
                              />
                            </div>
                            <div className="col-span-2">
                              <StateCitySelector
                                selectedState={tempPickupAddress.state}
                                selectedCity={tempPickupAddress.city}
                                onStateChange={(state) =>
                                  setTempPickupAddress(prev => ({ ...prev, state, city: "" }))
                                }
                                onCityChange={(city) =>
                                  setTempPickupAddress(prev => ({ ...prev, city }))
                                }
                                stateError={errors.pickup_state}
                                cityError={errors.pickup_city}
                                stateHasError={!!errors.pickup_state}
                                cityHasError={!!errors.pickup_city}
                                required={true}
                              />
                            </div>
                            <StandardFormInput
                              label="Postal Code"
                              required
                              value={tempPickupAddress.postal_code}
                              onChange={(e) =>
                                setTempPickupAddress(prev => ({ ...prev, postal_code: e.target.value }))
                              }
                              placeholder="6 digit PIN code"
                              error={errors.pickup_postal}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* 2. Customer Details Section */}
                <AccordionItem
                  value="customer"
                  className="bg-muted/20 rounded-lg border border-border/40 overflow-hidden"
                >
                  <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-muted/30">
                    <SectionHeader
                      icon={User}
                      title="Customer Details"
                      subtitle="Select or create a customer"
                      color="bg-green-500"
                      step={2}
                    />
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <div className="space-y-3">
                      <p className="text-xs text-muted-foreground flex items-center">
                        <Info className="h-3 w-3 mr-1" />
                        Search for an existing customer or create a new one for this order.
                      </p>

                      {/* Toggle between existing and new customer */}
                      <div className="flex items-center gap-4 mb-3">
                        <label className="flex items-center gap-2 cursor-pointer text-sm">
                          <input
                            type="radio"
                            checked={!isNewCustomer}
                            onChange={() => {
                              setIsNewCustomer(false);
                              setUseExistingAddress(true);
                            }}
                            className="w-4 h-4"
                          />
                          Existing Customer
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer text-sm">
                          <input
                            type="radio"
                            checked={isNewCustomer}
                            onChange={() => {
                              setIsNewCustomer(true);
                              setCustomerId("");
                              setCustomerDetails(null);
                              setSelectedCustomerAddresses([]);
                              setSelectedAddressId("");
                              setUseExistingAddress(false);
                            }}
                            className="w-4 h-4"
                          />
                          <span className="flex items-center gap-1">
                            New Customer
                            <Badge
                              variant="secondary"
                              className="text-[9px] h-4 bg-orange-500/15 text-orange-700 border border-orange-500/40 dark:bg-orange-500/25 dark:text-orange-50"
                            >
                              Create
                            </Badge>
                          </span>
                        </label>
                      </div>

                      {!isNewCustomer ? (
                        <>
                          <CustomerSelector
                            value={customerId}
                            onValueChange={setCustomerId}
                            onCustomerSelect={handleCustomerSelect}
                            error={errors.customer}
                          />
                          {customerDetails && (
                            <div className="bg-background border border-border rounded-lg p-3 space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium">
                                  {customerDetails.first_name} {customerDetails.last_name}
                                </span>
                                <Badge
                                  variant="secondary"
                                  className="text-[10px] bg-orange-500/15 text-orange-700 border border-orange-500/40 dark:bg-orange-500/25 dark:text-orange-50"
                                >
                                  {customerDetails.total_orders || 0} orders
                                </Badge>
                              </div>
                              {customerDetails.email && (
                                <p className="text-xs text-muted-foreground">{customerDetails.email}</p>
                              )}
                              {customerDetails.phone_number && (
                                <p className="text-xs text-muted-foreground">{customerDetails.phone_number}</p>
                              )}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="space-y-3">
                          <p className="text-xs font-medium text-foreground flex items-center gap-2">
                            <Plus className="h-3 w-3" />
                            New Customer Details
                          </p>
                          <div className="grid grid-cols-2 gap-3">
                            <StandardFormInput
                              label="First Name"
                              required
                              value={newCustomer.first_name}
                              onChange={(e) =>
                                setNewCustomer(prev => ({ ...prev, first_name: e.target.value }))
                              }
                              placeholder="First name"
                              error={errors.customer_first_name}
                            />
                            <StandardFormInput
                              label="Last Name"
                              value={newCustomer.last_name}
                              onChange={(e) =>
                                setNewCustomer(prev => ({ ...prev, last_name: e.target.value }))
                              }
                              placeholder="Last name"
                            />
                            <StandardFormInput
                              label="Phone Number"
                              required
                              value={newCustomer.phone_number}
                              onChange={(e) =>
                                setNewCustomer(prev => ({ ...prev, phone_number: e.target.value }))
                              }
                              placeholder="10 digit number"
                              error={errors.customer_phone}
                              helperText="Customer's contact number for delivery updates"
                            />
                            <StandardFormInput
                              label="Email"
                              type="email"
                              value={newCustomer.email}
                              onChange={(e) =>
                                setNewCustomer(prev => ({ ...prev, email: e.target.value }))
                              }
                              placeholder="email@example.com"
                              helperText="Optional - for order confirmation"
                            />
                          </div>
                          <p className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">
                            <Info className="h-3 w-3 inline mr-1" />
                            This customer will be created when you submit the order. You'll need to provide the delivery address in the next section.
                          </p>
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* 3. Delivery Details Section */}
                <AccordionItem
                  value="delivery"
                  className="bg-muted/20 rounded-lg border border-border/40 overflow-hidden"
                >
                  <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-muted/30">
                    <SectionHeader
                      icon={MapPin}
                      title="Delivery Details"
                      subtitle="Shipping address for the order"
                      color="bg-purple-500"
                      step={3}
                    />
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <div className="space-y-4">
                      <p className="text-xs text-muted-foreground flex items-center">
                        <Info className="h-3 w-3 mr-1" />
                        {isNewCustomer
                          ? "Enter the delivery address for the new customer."
                          : "Choose an existing customer address or create a new one."
                        }
                      </p>

                      {/* Show info banner for new customer */}
                      {isNewCustomer && (
                        <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                          <p className="text-xs text-blue-700 dark:text-blue-300">
                            <Info className="h-3 w-3 inline mr-1" />
                            Creating order for new customer: <span className="font-medium">{newCustomer.first_name} {newCustomer.last_name}</span>
                            {newCustomer.phone_number && <span> ({newCustomer.phone_number})</span>}
                          </p>
                        </div>
                      )}

                      {/* Toggle for existing customers with addresses */}
                      {!isNewCustomer && customerId && selectedCustomerAddresses.length > 0 && (
                        <div className="flex items-center gap-4 mb-3">
                          <label className="flex items-center gap-2 cursor-pointer text-sm">
                            <input
                              type="radio"
                              checked={useExistingAddress}
                              onChange={() => setUseExistingAddress(true)}
                              className="w-4 h-4"
                            />
                            Use Existing Address
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer text-sm">
                            <input
                              type="radio"
                              checked={!useExistingAddress}
                              onChange={() => setUseExistingAddress(false)}
                              className="w-4 h-4"
                            />
                            Add New Address
                          </label>
                        </div>
                      )}

                      {/* Show existing addresses for existing customers */}
                      {!isNewCustomer && useExistingAddress && selectedCustomerAddresses.length > 0 ? (
                        <RadioGroup
                          value={selectedAddressId}
                          onValueChange={setSelectedAddressId}
                          className="grid gap-2"
                        >
                          {selectedCustomerAddresses.map((addr) => (
                            <label
                              key={addr.id}
                              className={cn(
                                "flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all",
                                selectedAddressId === addr.id
                                  ? "border-primary bg-primary/5"
                                  : "border-border hover:border-primary/50 hover:bg-muted/30"
                              )}
                            >
                              <RadioGroupItem value={addr.id} className="mt-1" />
                              <div className="flex-1">
                                <p className="text-sm">{addr.address_line_one}</p>
                                {addr.address_line_two && (
                                  <p className="text-xs text-muted-foreground">{addr.address_line_two}</p>
                                )}
                                <p className="text-xs text-muted-foreground">
                                  {addr.city}, {addr.state} - {addr.postal_code}
                                </p>
                              </div>
                            </label>
                          ))}
                        </RadioGroup>
                      ) : (
                        /* New address form - shown for new customers or when adding new address */
                        <div className="space-y-2">
                          <p className="text-xs text-muted-foreground">
                            Recipient name and phone use the customer details above.
                          </p>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="col-span-2">
                              <StandardFormInput
                                label="Address Line 1"
                                required
                                value={newAddress.address_line_one}
                                onChange={(e) =>
                                  setNewAddress(prev => ({ ...prev, address_line_one: e.target.value }))
                                }
                                placeholder="House/flat number, street"
                                error={errors.address_line_one}
                              />
                            </div>
                            <div className="col-span-2">
                              <StandardFormInput
                                label="Address Line 2"
                                value={newAddress.address_line_two}
                                onChange={(e) =>
                                  setNewAddress(prev => ({ ...prev, address_line_two: e.target.value }))
                                }
                                placeholder="Apartment, landmark (optional)"
                              />
                            </div>
                            <div className="col-span-2">
                              <StateCitySelector
                                selectedState={newAddress.state}
                                selectedCity={newAddress.city}
                                onStateChange={(state) =>
                                  setNewAddress(prev => ({ ...prev, state, city: "" }))
                                }
                                onCityChange={(city) =>
                                  setNewAddress(prev => ({ ...prev, city }))
                                }
                                stateError={errors.state}
                                cityError={errors.city}
                                stateHasError={!!errors.state}
                                cityHasError={!!errors.city}
                                required={true}
                              />
                            </div>
                            <div className="col-span-2 md:col-span-1">
                              <StandardFormInput
                                label="Postal Code"
                                required
                                value={newAddress.postal_code}
                                onChange={(e) =>
                                  setNewAddress(prev => ({ ...prev, postal_code: e.target.value }))
                                }
                                placeholder="6 digit PIN code"
                                error={errors.postal_code}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                      {errors.address && (
                        <p className="text-red-500 text-xs">{errors.address}</p>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* 4. Product Details Section */}
                <AccordionItem
                  value="products"
                  className="bg-muted/20 rounded-lg border border-border/40 overflow-hidden"
                >
                  <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-muted/30">
                    <SectionHeader
                      icon={Package}
                      title="Product Details"
                      subtitle="Add products or create temporary items"
                      color="bg-orange-500"
                      step={4}
                    />
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <div className="space-y-4">
                      <p className="text-xs text-muted-foreground flex items-center">
                        <Info className="h-3 w-3 mr-1" />
                        Select existing products or create temporary items for one-time orders.
                      </p>

                      <ProductSelector
                        value={selectedProducts}
                        onValueChange={setSelectedProducts}
                        multiSelect={true}
                        error={errors.products}
                      />

                      {/* Add Temporary Product */}
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">
                          Or add a temporary product for this order only
                        </span>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setShowAddTempProduct(!showAddTempProduct)}
                          className="h-7 text-xs"
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Add Temp Product
                        </Button>
                      </div>

                      {showAddTempProduct && (
                        <div className="bg-background border border-border rounded-lg p-4 space-y-3">
                          <p className="text-xs font-medium text-foreground">New Temporary Product</p>
                          <div className="grid grid-cols-2 gap-3">
                            <StandardFormInput
                              label="Product Name"
                              required
                              value={tempProduct.name}
                              onChange={(e) =>
                                setTempProduct({ ...tempProduct, name: e.target.value })
                              }
                              placeholder="Product name"
                            />
                            <StandardFormInput
                              label="SKU"
                              value={tempProduct.sku}
                              onChange={(e) =>
                                setTempProduct({ ...tempProduct, sku: e.target.value })
                              }
                              placeholder="SKU (optional)"
                            />
                            <StandardFormInput
                              label="Unit Price (paise)"
                              required
                              type="number"
                              value={tempProduct.unit_price.toString()}
                              onChange={(e) =>
                                setTempProduct({
                                  ...tempProduct,
                                  unit_price: parseInt(e.target.value) || 0,
                                })
                              }
                              placeholder="Price in paise"
                              helperText="Enter price in paise (100 paise = ₹1)"
                            />
                            <StandardFormInput
                              label="Quantity"
                              required
                              type="number"
                              min="1"
                              value={tempProduct.quantity.toString()}
                              onChange={(e) =>
                                setTempProduct({
                                  ...tempProduct,
                                  quantity: parseInt(e.target.value) || 1,
                                })
                              }
                              placeholder="Quantity"
                            />
                            <StandardFormInput
                              label="Tax Rate (%)"
                              type="number"
                              value={tempProduct.tax_rate.toString()}
                              onChange={(e) =>
                                setTempProduct({
                                  ...tempProduct,
                                  tax_rate: parseFloat(e.target.value) || 0,
                                })
                              }
                              placeholder="GST rate"
                              helperText="Enter GST percentage (e.g., 18 for 18%)"
                            />
                            <StandardFormInput
                              label="HSN Code"
                              value={tempProduct.hsn_code}
                              onChange={(e) =>
                                setTempProduct({ ...tempProduct, hsn_code: e.target.value })
                              }
                              placeholder="HSN/SAC code"
                              helperText="8-digit code for tax classification"
                            />
                          </div>
                          <div className="flex justify-end gap-2 pt-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setShowAddTempProduct(false)}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              onClick={addTempProduct}
                              disabled={!tempProduct.name || tempProduct.unit_price <= 0}
                            >
                              Add Product
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Product Line Items */}
                      {productLineItems.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-foreground">Order Items</p>
                          <div className="border border-border rounded-lg overflow-hidden">
                            <div className="bg-muted/50 px-3 py-2 grid gap-2 text-xs font-medium text-muted-foreground" style={{ gridTemplateColumns: '200px 120px 80px 100px 70px 100px 40px' }}>
                              <div>Product</div>
                              <div>Variant</div>
                              <div>Qty</div>
                              <div>Price</div>
                              <div>Tax %</div>
                              <div>HSN</div>
                              <div></div>
                            </div>
                            <div className="divide-y divide-border">
                              {productLineItems.map((item) => (
                                <div
                                  key={item.id}
                                  className="px-3 py-2 grid gap-2 items-center text-sm"
                                  style={{ gridTemplateColumns: '200px 120px 80px 100px 70px 100px 40px' }}
                                >
                                  <div className="flex items-start gap-2">
                                    {item.is_temporary && (
                                      <Badge
                                        variant="secondary"
                                        className="text-[9px] h-4 mt-[2px] bg-orange-500/15 text-orange-700 border border-orange-500/40 dark:bg-orange-500/25 dark:text-orange-50"
                                      >
                                        Temp
                                      </Badge>
                                    )}
                                    <div className="flex-1 min-w-0 space-y-0.5">
                                      <p className="truncate text-xs font-medium">
                                        {item.is_temporary
                                          ? item.temp_name
                                          : item.product_title ||
                                            productTitles[item.product_id] ||
                                            "Product"}
                                      </p>
                                      {!item.is_temporary && (
                                        <p className="truncate text-[11px] text-muted-foreground">
                                          {item.variant_title ||
                                            getVariantLabel(
                                              availableVariants[item.product_id]?.find(
                                                (variant) => variant.id === item.variant_id
                                              ) || null
                                            )}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  <div>
                                    {!item.is_temporary && availableVariants[item.product_id]?.length > 1 ? (
                                      <select
                                        value={item.variant_id}
                                        onChange={(e) =>
                                          updateLineItem(item.id, "variant_id", e.target.value)
                                        }
                                        className="w-full h-7 px-2 text-xs bg-background border border-border rounded"
                                      >
                                        {availableVariants[item.product_id]?.map((variant) => (
                                          <option key={variant.id} value={variant.id}>
                                            {getVariantLabel(variant)}
                                          </option>
                                        ))}
                                      </select>
                                    ) : (
                                      <span className="text-xs text-muted-foreground">-</span>
                                    )}
                                  </div>
                                  <div>
                                    <input
                                      type="number"
                                      min="1"
                                      value={item.quantity}
                                      onChange={(e) =>
                                        updateLineItem(
                                          item.id,
                                          "quantity",
                                          parseInt(e.target.value) || 1
                                        )
                                      }
                                      className="w-full h-7 px-2 text-xs bg-background border border-border rounded text-center"
                                    />
                                  </div>
                                  <div>
                                    <input
                                      type="number"
                                      value={item.unit_price}
                                      onChange={(e) =>
                                        updateLineItem(
                                          item.id,
                                          "unit_price",
                                          parseInt(e.target.value) || 0
                                        )
                                      }
                                      className="w-full h-7 px-2 text-xs bg-background border border-border rounded"
                                    />
                                  </div>
                                  <div>
                                    <input
                                      type="number"
                                      value={item.tax_rate}
                                      onChange={(e) =>
                                        updateLineItem(
                                          item.id,
                                          "tax_rate",
                                          parseFloat(e.target.value) || 0
                                        )
                                      }
                                      className="w-full h-7 px-2 text-xs bg-background border border-border rounded text-center"
                                    />
                                  </div>
                                  <div>
                                    <input
                                      type="text"
                                      value={item.hsn_code}
                                      onChange={(e) =>
                                        updateLineItem(item.id, "hsn_code", e.target.value)
                                      }
                                      placeholder="HSN"
                                      className="w-full h-7 px-2 text-xs bg-background border border-border rounded"
                                    />
                                  </div>
                                  <div className="flex justify-end">
                                    <button
                                      type="button"
                                      onClick={() => removeLineItem(item.id)}
                                      className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 rounded"
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* 5. Additional Charges Section */}
                <AccordionItem
                  value="charges"
                  className="bg-muted/20 rounded-lg border border-border/40 overflow-hidden"
                >
                  <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-muted/30">
                    <SectionHeader
                      icon={DollarSign}
                      title="Additional Charges"
                      subtitle="Shipping, delivery, and other charges"
                      color="bg-yellow-500"
                      step={5}
                    />
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <div className="space-y-4">
                      <p className="text-xs text-muted-foreground flex items-center">
                        <Info className="h-3 w-3 mr-1" />
                        Add any additional charges or discounts to the order.
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        <StandardFormInput
                          label="Shipping Charge (paise)"
                          type="number"
                          value={additionalCharges.shipping_charge.toString()}
                          onChange={(e) =>
                            setAdditionalCharges({
                              ...additionalCharges,
                              shipping_charge: parseInt(e.target.value) || 0,
                            })
                          }
                          placeholder="0"
                          helperText="Base shipping cost charged to customer"
                        />
                        <StandardFormInput
                          label="Delivery Charge (paise)"
                          type="number"
                          value={additionalCharges.delivery_charge.toString()}
                          onChange={(e) =>
                            setAdditionalCharges({
                              ...additionalCharges,
                              delivery_charge: parseInt(e.target.value) || 0,
                            })
                          }
                          placeholder="0"
                          helperText="Additional delivery fee (e.g., express delivery)"
                        />
                        <StandardFormInput
                          label="Gift Wrap Charge (paise)"
                          type="number"
                          value={additionalCharges.gift_wrap_charge.toString()}
                          onChange={(e) =>
                            setAdditionalCharges({
                              ...additionalCharges,
                              gift_wrap_charge: parseInt(e.target.value) || 0,
                            })
                          }
                          placeholder="0"
                          helperText="Gift wrapping service fee"
                        />
                        <StandardFormInput
                          label="Transaction Charge (paise)"
                          type="number"
                          value={additionalCharges.transaction_charge.toString()}
                          onChange={(e) =>
                            setAdditionalCharges({
                              ...additionalCharges,
                              transaction_charge: parseInt(e.target.value) || 0,
                            })
                          }
                          placeholder="0"
                          helperText="Payment gateway or COD handling fee"
                        />
                      </div>
                      <div className="border-t border-border pt-4">
                        <p className="text-xs font-medium text-foreground mb-3">Order Discount</p>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-[13px] font-normal text-muted-foreground mb-1">
                              Discount Type
                            </label>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() =>
                                  setAdditionalCharges({
                                    ...additionalCharges,
                                    discount_type: "fixed",
                                  })
                                }
                                className={cn(
                                  "flex-1 py-2 px-3 text-xs rounded-lg border transition-all",
                                  additionalCharges.discount_type === "fixed"
                                    ? "bg-primary text-primary-foreground border-primary"
                                    : "bg-background border-border hover:bg-muted"
                                )}
                              >
                                Fixed (₹)
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  setAdditionalCharges({
                                    ...additionalCharges,
                                    discount_type: "percentage",
                                  })
                                }
                                className={cn(
                                  "flex-1 py-2 px-3 text-xs rounded-lg border transition-all",
                                  additionalCharges.discount_type === "percentage"
                                    ? "bg-primary text-primary-foreground border-primary"
                                    : "bg-background border-border hover:bg-muted"
                                )}
                              >
                                Percentage (%)
                              </button>
                            </div>
                          </div>
                          <StandardFormInput
                            label={
                              additionalCharges.discount_type === "percentage"
                                ? "Discount (%)"
                                : "Discount (paise)"
                            }
                            type="number"
                            value={additionalCharges.total_discount.toString()}
                            onChange={(e) =>
                              setAdditionalCharges({
                                ...additionalCharges,
                                total_discount: parseFloat(e.target.value) || 0,
                              })
                            }
                            placeholder="0"
                            helperText="Order-level discount applied to subtotal"
                          />
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* 6. Payment Method Section */}
                <AccordionItem
                  value="payment"
                  className="bg-muted/20 rounded-lg border border-border/40 overflow-hidden"
                >
                  <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-muted/30">
                    <SectionHeader
                      icon={CreditCard}
                      title="Payment Method"
                      subtitle="How the customer will pay"
                      color="bg-pink-500"
                      step={6}
                    />
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <div className="space-y-4">
                      <p className="text-xs text-muted-foreground flex items-center">
                        <Info className="h-3 w-3 mr-1" />
                        Choose the payment method for this order.
                      </p>
                      <div className="grid grid-cols-3 gap-3">
                        {[
                          {
                            value: "PREPAID",
                            label: "Prepaid",
                            description: "Full payment received online",
                          },
                          {
                            value: "POSTPAID",
                            label: "COD",
                            description: "Cash on delivery",
                          },
                          {
                            value: "SEMIPREPAID",
                            label: "Semi Prepaid",
                            description: "Partial payment upfront",
                          },
                        ].map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() =>
                              setPaymentType(
                                option.value as "PREPAID" | "POSTPAID" | "SEMIPREPAID"
                              )
                            }
                            className={cn(
                              "p-3 rounded-lg border text-left transition-all",
                              paymentType === option.value
                                ? "bg-primary/10 border-primary"
                                : "bg-background border-border hover:bg-muted/30 hover:border-primary/50"
                            )}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <div
                                className={cn(
                                  "w-4 h-4 rounded-full border-2 flex items-center justify-center",
                                  paymentType === option.value
                                    ? "border-primary bg-primary"
                                    : "border-muted-foreground"
                                )}
                              >
                                {paymentType === option.value && (
                                  <Check className="h-2.5 w-2.5 text-white" />
                                )}
                              </div>
                              <span className="text-sm font-medium">{option.label}</span>
                            </div>
                            <p className="text-xs text-muted-foreground ml-6">
                              {option.description}
                            </p>
                          </button>
                        ))}
                      </div>
                      {paymentType === "SEMIPREPAID" && (
                        <div className="border border-border rounded-lg p-3">
                          <StandardFormInput
                            label="Prepaid Amount (₹)"
                            type="number"
                            value={semiPrepaidAmount.toString()}
                            onChange={(e) =>
                              setSemiPrepaidAmount(parseFloat(e.target.value) || 0)
                            }
                            placeholder="Enter prepaid amount in rupees"
                            helperText="Amount already received from customer (converted to paise internally)"
                          />
                          <p className="text-xs text-muted-foreground mt-2">
                            Balance due on delivery: {" "}
                            <span className="font-medium text-foreground">
                              {formatCurrency(
                                Math.max(0, orderCalculations.grandTotal - semiPrepaidPaise)
                              )}
                            </span>
                          </p>
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>

              </Accordion>
            </div>

            {/* Summary Sidebar */}
            <div className="w-80 flex-shrink-0 border-l border-border bg-muted/20 p-4 overflow-y-auto">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Receipt className="h-4 w-4 text-muted-foreground" />
                  <h3 className="font-medium text-foreground">Order Summary</h3>
                </div>

                {/* Quick Status */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-xs">
                    <div
                      className={cn(
                        "w-2 h-2 rounded-full",
                        (useTempPickup && tempPickupAddress.address_line_one) || selectedPickupAddressId
                          ? "bg-green-500"
                          : "bg-gray-300"
                      )}
                    />
                    <span className="text-muted-foreground">
                      Pickup Address
                      {useTempPickup && (
                        <Badge
                          variant="secondary"
                          className="text-[8px] h-3 ml-1 bg-orange-500/15 text-orange-700 border border-orange-500/40 dark:bg-orange-500/25 dark:text-orange-50"
                        >
                          Temp
                        </Badge>
                      )}
                    </span>
                    {((useTempPickup && tempPickupAddress.address_line_one) || selectedPickupAddressId) && (
                      <Check className="h-3 w-3 text-green-500 ml-auto" />
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <div
                      className={cn(
                        "w-2 h-2 rounded-full",
                        (isNewCustomer && newCustomer.first_name && newCustomer.phone_number) || customerId
                          ? "bg-green-500"
                          : "bg-gray-300"
                      )}
                    />
                    <span className="text-muted-foreground">
                      Customer
                      {isNewCustomer && (
                        <Badge
                          variant="secondary"
                          className="text-[8px] h-3 ml-1 bg-orange-500/15 text-orange-700 border border-orange-500/40 dark:bg-orange-500/25 dark:text-orange-50"
                        >
                          New
                        </Badge>
                      )}
                    </span>
                    {((isNewCustomer && newCustomer.first_name && newCustomer.phone_number) || customerId) && (
                      <Check className="h-3 w-3 text-green-500 ml-auto" />
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <div
                      className={cn(
                        "w-2 h-2 rounded-full",
                        (!isNewCustomer && useExistingAddress && selectedAddressId) ||
                          ((!useExistingAddress || isNewCustomer) && newAddress.address_line_one)
                          ? "bg-green-500"
                          : "bg-gray-300"
                      )}
                    />
                    <span className="text-muted-foreground">Delivery Address</span>
                    {((!isNewCustomer && useExistingAddress && selectedAddressId) ||
                      ((!useExistingAddress || isNewCustomer) && newAddress.address_line_one)) && (
                      <Check className="h-3 w-3 text-green-500 ml-auto" />
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <div
                      className={cn(
                        "w-2 h-2 rounded-full",
                        productLineItems.length > 0 ? "bg-green-500" : "bg-gray-300"
                      )}
                    />
                    <span className="text-muted-foreground">Products</span>
                    {productLineItems.length > 0 && (
                      <span className="text-xs text-muted-foreground ml-auto">
                        {productLineItems.length} item(s)
                      </span>
                    )}
                  </div>
                </div>

                <div className="border-t border-border pt-4 space-y-3">
                  {/* Items Summary */}
                  {productLineItems.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground">ITEMS</p>
                      {productLineItems.slice(0, 3).map((item) => (
                        <div key={item.id} className="flex justify-between text-xs">
                          <span className="truncate max-w-[150px]">
                            {item.is_temporary ? item.temp_name : item.variant_title} x{item.quantity}
                          </span>
                          <span>{formatCurrency(item.unit_price * item.quantity)}</span>
                        </div>
                      ))}
                      {productLineItems.length > 3 && (
                        <p className="text-xs text-muted-foreground">
                          +{productLineItems.length - 3} more items
                        </p>
                      )}
                    </div>
                  )}

                  {/* Calculations */}
                  <div className="border-t border-border pt-3 space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span>{formatCurrency(orderCalculations.itemsSubtotal)}</span>
                    </div>
                    {orderCalculations.totalItemDiscount > 0 && (
                      <div className="flex justify-between text-xs text-green-600">
                        <span>Item Discount</span>
                        <span>-{formatCurrency(orderCalculations.totalItemDiscount)}</span>
                      </div>
                    )}
                    {orderCalculations.orderDiscount > 0 && (
                      <div className="flex justify-between text-xs text-green-600">
                        <span>Order Discount</span>
                        <span>-{formatCurrency(orderCalculations.orderDiscount)}</span>
                      </div>
                    )}
                    {orderCalculations.totalTax > 0 && (
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Tax</span>
                        <span>+{formatCurrency(orderCalculations.totalTax)}</span>
                      </div>
                    )}
                    {orderCalculations.totalCharges > 0 && (
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Charges</span>
                        <span>+{formatCurrency(orderCalculations.totalCharges)}</span>
                      </div>
                    )}
                  </div>

                  {/* Grand Total */}
                  <div className="border-t border-border pt-3">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Grand Total</span>
                      <span className="text-lg font-bold text-primary">
                        {formatCurrency(orderCalculations.grandTotal)}
                      </span>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Payment: {paymentType === "PREPAID" ? "Prepaid" : paymentType === "POSTPAID" ? "COD" : "Semi Prepaid"}
                    </p>
                  </div>

                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border/60 bg-muted/30 flex-shrink-0">
            <Button
              variant="outline"
              onClick={handleClose}
              className="text-sm h-9 px-4 border-red-200 bg-red-50 text-red-600 hover:bg-red-100 hover:border-red-300 dark:border-red-800 dark:bg-red-950 dark:text-red-400 dark:hover:bg-red-900 dark:hover:border-red-700 rounded-lg"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={productLineItems.length === 0}
              className="bg-primary text-primary-foreground hover:bg-primary/90 text-sm h-9 px-6 shadow-md rounded-lg"
            >
              Create Order
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
